import { type PagamentoWithDettaglioDTO } from '@framework/api-client/api/inquiries-compile/schemas/pagamentoWithDettaglioDTO.ts';
import { assertNever } from '@framework/engine/utils/assertNever.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { StyledTag } from '@framework/ui/components/tag/StyledTag.tsx';
import { Space, type TagProps } from 'antd';
import { type DefaultTheme, useTheme } from 'styled-components';

interface PaymentBurdenTypeTagsProps {
  payment: PagamentoWithDettaglioDTO;
}

type BurdenType = 'bollo' | 'modello 1' | 'modello 3';

export function PaymentBurdenTypeTags(props: PaymentBurdenTypeTagsProps) {
  const { payment } = props;
  const burdenTypes = new Set<BurdenType>();

  // N.B. Oneri di tipo bollo non sono cumulabili con oneri di altro tipo
  payment.oneri?.forEach(o => {
    if (o.tipoOnere?.flPagamentoBollo) {
      burdenTypes.add('bollo');
    }
    if (o.tipoOnere?.flPagamentoModello1) {
      burdenTypes.add('modello 1');
    }
    if (
      o.tipoOnere?.flPagamentoModello3 ||
      o.tipoOnere?.flPagamentoModello3Asincrono
    ) {
      burdenTypes.add('modello 3');
    }
  });

  if (payment.preventivo?.tipoPagamento?.flPagamentoModello1) {
    burdenTypes.add('modello 1');
  }
  if (payment.preventivo?.tipoPagamento?.flPagamentoModello3) {
    burdenTypes.add('modello 3');
  }

  return (
    <Space>
      {Array.from(burdenTypes)
        .sort((a, b) => a.localeCompare(b))
        .map(type => (
          <PaymentBurdenTypeTag key={type} type={type} />
        ))}
    </Space>
  );
}

interface PaymentBurdenTypeTagProps {
  type: BurdenType;
}

function PaymentBurdenTypeTag(props: PaymentBurdenTypeTagProps) {
  const { type } = props;
  const theme = useTheme();
  return <StyledTag {...getTagOptions(type, theme)} />;
}

function getTagOptions(type: BurdenType, theme?: DefaultTheme): TagProps {
  switch (type) {
    case 'bollo':
      return {
        color: theme?.colorInfo,
        children: t('payment-burden-type-tag.bollo', type),
      };
    case 'modello 1':
      return {
        color: theme?.colorSuccess,
        children: t('payment-burden-type-tag.modello-1', type),
      };
    case 'modello 3':
      return {
        color: theme?.colorSuccess,
        children: t('payment-burden-type-tag.modello-3', type),
      };
    default:
      assertNever(type);
  }
}
